import React, { useRef, useEffect, useContext } from 'react';
import GlobalContext from 'hooks/contexts/GlobalContext';
import {
  SchoolFilters,
  MultiRowSchoolListingResults,
  ErrorPriority,
  ClickPortalResultRowsSectionProps,
} from 'types';
import {
  handleSchoolResultsRequestWithUpdates,
  initImpressionDebugging,
} from 'app-requests/clickPortalSchoolManager';
import { SCHOOL_IMPRESSION_TYPES } from 'consts';
import { getUserRegionCookie } from 'utils/analyticsHelpers';
import { LogError } from 'utils/logging/LogError';
import { LogInfo } from 'utils/logging';
import { EventType } from 'hooks/custom/UpdateUserProfileRequestType';
import { transformResultRowsConfigToSchoolFilters } from 'utils/transformResultRowsConfigToSchoolFilters';
import Wysiwyg from 'components/blocks/wysiwyg/Wysiwyg';
import { useProfileUpdater } from 'hooks/custom/useProfileUpdater';
import ClickPortalResultRowsLoader from 'components/sections/clickPortalResultRows/ClickPortalResultRowsLoader';
import ClickPortalResultRowsList from './ClickPortalResultRowsList';

export default function ClickPortalResultRowsSection(
  props: ClickPortalResultRowsSectionProps
): React.JSX.Element {
  const {
    title,
    description,
    resultRowsConfig,
    isCurated,
    mobileTitle,
    initalResults,
  } = props;
  const sectionRefs = useRef<(HTMLDivElement | null)[]>(
    resultRowsConfig?.map(() => null) || []
  );

  const { sendProfileUpdate, setPageViewIdTaxonomyMap } = useProfileUpdater();

  const {
    siteMeta: { schoolCode, siteType, clickPortalSettings },
    sessionInfo: { userSessionId, pageViewId },
    actions: { updateClickPortalData },
    clickPortal: { resultRows, isLoading },
    windowSize: { currentBreakpoint },
  } = useContext(GlobalContext);

  /**
   * This handles the request of school listing and mapping of its impression guids
   */
  async function handleSchoolResultsRequest(
    filters: SchoolFilters[],
    meta: { originalUrl: string } = { originalUrl: '' }
  ): Promise<MultiRowSchoolListingResults> {
    return handleSchoolResultsRequestWithUpdates(
      filters,
      siteType,
      userSessionId || '', // Pass userSessionId from context, fallback to empty string
      {
        isPersonalized: Boolean(getUserRegionCookie()),
        originalUrl: meta.originalUrl,
        schoolCode,
        isMultiRow: clickPortalSettings?.isMultiRow,
        isCurated,
        geoLocation: {},
        queryParams: {},
        cookies: {},
        // we implemented redis cach so we should never fetch impressions
        impressionType:
          SCHOOL_IMPRESSION_TYPES.CP_REQUEST_IMPRESSIONS_CACHE_KEY,
      },
      (data) => {
        updateClickPortalData(data);
      }
    );
  }

  const _description =
    description ||
    (currentBreakpoint === 'mobile'
      ? clickPortalSettings.searchHeaderDescription.mobile
      : clickPortalSettings.searchHeaderDescription.desktop);

  useEffect(() => {
    if (!isLoading) {
      setPageViewIdTaxonomyMap(/* resetIfNoneSet */ true);
      sendProfileUpdate({
        eventType: EventType.SCHOOL_LISTING_LOADED,
      });
    }
  }, [isLoading]);

  useEffect(() => {
    LogInfo('ClickPortalResultRowsSection: Initializing impression debugging');
    initImpressionDebugging();
  }, []);

  useEffect(() => {
    /** Although userSessionId is not needed because we call for impression after results come back if needed
     * if we wait for sessionId this might improve impression tracking. It seems 15% of users are not having
     * impressions tracked. This might be due to them bouncing before the impression tracking is complete.
     * BUG: [FSIU-200] One imprtant thing to note is there is a bug in the superfetch retry. if session id is not in the getImpression
     * can fail and the retries still wont have the session id. That needs to be fixed before removing this.
     */
    if (!userSessionId || !pageViewId) {
      return;
    }

    try {
      const requestRows =
        transformResultRowsConfigToSchoolFilters(resultRowsConfig);

      if (!requestRows.length) {
        return;
      }

      handleSchoolResultsRequest(requestRows, {
        originalUrl: window.location.pathname,
      });
    } catch (error) {
      LogError(error as Error, {
        priority: ErrorPriority.ONE,
        description: 'Error fetching click portal result rows from pageSection',
      });
    }
  }, [resultRowsConfig, userSessionId, pageViewId]);

  let rowsToRender: MultiRowSchoolListingResults['resultRows'] = [];

  if (!isLoading && resultRows.length > 0) {
    rowsToRender = resultRows;
  } else {
    rowsToRender = initalResults?.resultRows || [];
  }

  return (
    <section className="clickPortalResultRowsSection clickPortalResultRowsSection--isMultiRow">
      <div className="clickPortalResultRowsSection__inner">
        <div className="clickPortalResultRowsSection__header">
          <h2 className="clickPortalResultRowsSection__title">
            {currentBreakpoint === 'mobile' && mobileTitle
              ? mobileTitle
              : title}
          </h2>
        </div>
        {_description && (
          <div
            className="clickPortalResultRowsSection__description"
            data-testid="clickPortalResultRowsSection__description"
          >
            <Wysiwyg isClean content={_description} />
          </div>
        )}
        {rowsToRender.map((resultRow, index) => (
          <ClickPortalResultRowsList
            key={`${resultRow.title}-${resultRow.subTitle || index}-${
              resultRow.schoolImpressionGroupGuid
            }`}
            resultRow={resultRow}
            wpTitle={resultRowsConfig[index]?.title}
            wpSubTitle={resultRowsConfig[index]?.subTitle}
            scrollRef={{ current: sectionRefs.current[index] }}
            rowIndex={index}
            isLoading={isLoading}
          />
        ))}
        <ClickPortalResultRowsLoader isLoading={isLoading} />
        <ClickPortalResultRowsLoader isLoading={isLoading} />
        <ClickPortalResultRowsLoader isLoading={isLoading} />
      </div>
    </section>
  );
}
